import React from 'react';
import { profileContent } from '../data/profileContent';

function ResultsPage({ profile, profileData, userData, answers, onRestart }) {

  const redirectToVINMETHOD = () => {
    const profileSlug = {
      'strategicArchitect': 'strategic-architect',
      'dailyExecutor': 'daily-executor',
      'balancedHarmonizer': 'balanced-harmonizer',
      'projectJuggler': 'project-juggler',
      'intuitiveAdapter': 'intuitive-adapter',
      'structuredMaximizer': 'structured-maximizer'
    }[profile];
    
    window.open(
      `https://reclaim.thefunctionalplanning.com?utm_source=quiz&utm_medium=results-page&utm_campaign=planning-dna&utm_content=${profileSlug}`,
      '_blank'
    );
  };

  const redirectTo3BrainSystem = () => {
    const profileSlug = {
      'strategicArchitect': 'strategic-architect',
      'dailyExecutor': 'daily-executor',
      'balancedHarmonizer': 'balanced-harmonizer',
      'projectJuggler': 'project-juggler',
      'intuitiveAdapter': 'intuitive-adapter',
      'structuredMaximizer': 'structured-maximizer'
    }[profile];

    window.open(
      `https://shop.thefunctionalplanning.com/en-myr/products/the-3-brain-planning-system-organize-your-life-in-7-days?utm_source=quiz&utm_medium=results-page&utm_campaign=planning-dna&utm_content=${profileSlug}`,
      '_blank'
    );
  };

  // Get the full profile content
  const fullProfileText = profileContent[profile]?.fullContent || '';

  // Get personalized insert recommendations based on answers
  const getInsertRecommendations = () => {
    const essential = [];
    const recommended = [];
    const growInto = [];

    // Project Tracker
    if (answers[23] === "Always - I always have multiple projects going" || 
        answers[23] === "Often - I usually have 1-2 ongoing projects") {
      essential.push({
        name: "Project Tracker",
        icon: "📊",
        reason: "You frequently work on long-term projects and need visibility across multiple initiatives."
      });
    }

    // Quarterly Calendar
    if (answers[19]?.includes("Quarterly goals/projects") || 
        answers[20] === "I think in quarters - 3-month chunks") {
      essential.push({
        name: "Quarterly Calendar",
        icon: "📅",
        reason: "You think in quarterly chunks and need a 3-month view for strategic planning."
      });
    }

    // Goal Map / Annual Planner
    if (answers[19]?.includes("Yearly goals and big picture") || 
        answers[20] === "I think in years - big picture goals") {
      essential.push({
        name: "Goal Map & Annual Planner",
        icon: "🎯",
        reason: "You need the big picture first - annual goals that cascade into quarterly milestones."
      });
    }

    // Monthly Overview
    if (answers[19]?.includes("Monthly overview") || 
        answers[22] === "Multiple per day" || 
        answers[22] === "Several per week") {
      recommended.push({
        name: "Monthly Overview",
        icon: "📆",
        reason: "You have regular appointments and need to see the whole month at a glance."
      });
    }

    // Weekly Planner
    if (answers[19]?.includes("Weekly planning") || 
        answers[20] === "I think in weeks - weekly rhythm") {
      recommended.push({
        name: "Weekly Planner",
        icon: "📋",
        reason: "Weekly planning is your natural rhythm - seeing the whole week helps you balance everything."
      });
    }

    // Daily Planner
    if (answers[19]?.includes("Daily task lists") || 
        answers[19]?.includes("Hourly time blocking") ||
        answers[20] === "I think in days - just today's priorities" ||
        answers[20] === "I think in hours - time-blocking my day") {
      if (profile === 'dailyExecutor' || profile === 'structuredMaximizer') {
        essential.push({
          name: "Daily Planner",
          icon: "📝",
          reason: "You thrive on daily structure and detailed planning - this is essential for your brain type."
        });
      } else {
        recommended.push({
          name: "Daily Planner",
          icon: "📝",
          reason: "Helpful for your busiest weeks when you need extra structure."
        });
      }
    }

    // Meal Planner
    if (answers[6]?.includes("Meal planning") || 
        answers[17] === "50-75% (Others control most of my time)" ||
        answers[17] === "75-100% (I'm constantly responding to others)") {
      growInto.push({
        name: "Meal Planner",
        icon: "🍽️",
        reason: "Eliminates daily decision fatigue about meals - frees mental energy for what matters most."
      });
    }

    // Task Map
    growInto.push({
      name: "Task Map",
      icon: "✓",
      reason: "For breaking down multi-step projects into actionable tasks."
    });

    // Routines Tracker
    if (answers[16] === "Very structured - same routine most days" || 
        answers[16] === "Moderately structured - some consistent elements") {
      growInto.push({
        name: "Routines Tracker",
        icon: "📓",
        reason: "Documents your routines once so they become automatic and require no mental energy."
      });
    }

    // Ensure we have at least 3 essential items
    while (essential.length < 3 && recommended.length > 0) {
      essential.push(recommended.shift());
    }

    // If still not 3, add from growInto
    while (essential.length < 3 && growInto.length > 0) {
      essential.push(growInto.shift());
    }

    return { essential, recommended, growInto };
  };

  const recommendations = getInsertRecommendations();

  return (
    <div className="results-page">
      <div className="container">
        {/* Header */}
        <div className="results-header">
          <h1 className="results-title">Your Planning DNA Results</h1>
          <p className="results-subtitle">
            {userData.firstName}, here's what we discovered about your planning style:
          </p>
        </div>

        {/* Profile Box */}
        <div className="profile-box">
          <div className="profile-badge">🎯 YOUR PLANNING PROFILE</div>
          <h2 className="profile-name">{profileData.name}</h2>
          <p className="profile-subtitle">{profileData.subtitle}</p>
          <p className="profile-summary">{profileData.quickSummary}</p>
          
          <div className="profile-stats">
            <div className="stat-item">
              <span className="stat-label">Roles You Manage:</span>
              <span className="stat-value">{answers[9]?.length || 0} life roles</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Natural Planning Style:</span>
              <span className="stat-value">{answers[20]}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Daily Focused Time:</span>
              <span className="stat-value">{answers[14]}</span>
            </div>
          </div>
        </div>

        {/* Full Profile Content */}
        <div className="profile-content">
          <div className="profile-text">
            {fullProfileText.split('\n\n').map((paragraph, index) => (
              <p key={index} className="profile-paragraph">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {/* Personalized Recommendations */}
        <div className="recommendations-section">
          <div className="recommendations-header">
            <h2 className="section-title">🎯 PERSONALIZED FOR YOUR PLANNING DNA</h2>
            <p className="section-intro">
              Based on your profile as {profileData.name}, here's which of the 12 inserts you should PRIORITIZE:
            </p>
          </div>

          {/* Essential Inserts */}
          <div className="recommendations-tier">
            <h3 className="tier-title">✅ START WITH THESE (Essential for your brain type)</h3>
            <div className="insert-list">
              {recommendations.essential.map((insert, index) => (
                <div key={index} className="insert-item">
                  <div className="insert-icon">{insert.icon}</div>
                  <div className="insert-content">
                    <h4 className="insert-name">{insert.name}</h4>
                    <p className="insert-reason">Why: {insert.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended Inserts */}
          {recommendations.recommended.length > 0 && (
            <div className="recommendations-tier">
              <h3 className="tier-title">⭐ ADD THESE NEXT (High impact for you)</h3>
              <div className="insert-list">
                {recommendations.recommended.map((insert, index) => (
                  <div key={index} className="insert-item">
                    <div className="insert-icon">{insert.icon}</div>
                    <div className="insert-content">
                      <h4 className="insert-name">{insert.name}</h4>
                      <p className="insert-reason">{insert.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grow Into Inserts */}
          {recommendations.growInto.length > 0 && (
            <div className="recommendations-tier">
              <h3 className="tier-title">💡 GROW INTO THESE (As your system matures)</h3>
              <div className="grow-into-note">
                <p>
                  The beauty of the complete system? You have ALL 12 inserts from day one. 
                  Start with your priority {recommendations.essential.length}, then gradually 
                  incorporate the others as you master the VIN METHOD.
                </p>
                <div className="additional-inserts">
                  {recommendations.growInto.map((insert, index) => (
                    <span key={index} className="additional-insert">
                      {insert.icon} {insert.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* VIN METHOD CTA */}
        <div className="vin-method-cta">
          <h2 className="cta-title">🌟 YOUR PERSONALIZED TRANSFORMATION PATH</h2>
          <p className="cta-intro">
            Now that you know WHAT you need, learn HOW to make it work with the VIN METHOD Complete System.
          </p>

          <div className="what-you-get">
            <h3 className="whats-included-title">✅ WHAT YOU GET:</h3>
            <div className="package-items">
              <div className="package-item">
                <span className="item-number">1.</span>
                <div className="item-content">
                  <strong>VIN METHOD Ebook</strong> - Complete work-life harmony methodology
                </div>
              </div>
              <div className="package-item">
                <span className="item-number">2.</span>
                <div className="item-content">
                  <strong>BONUS #1: Workbook</strong> - Self-assessment tools + implementation worksheets
                </div>
              </div>
              <div className="package-item">
                <span className="item-number">3.</span>
                <div className="item-content">
                  <strong>BONUS #2: 30-Day Quick Start</strong> - Fast-track implementation roadmap
                </div>
              </div>
              <div className="package-item">
                <span className="item-number">4.</span>
                <div className="item-content">
                  <strong>BONUS #3: 12 Complete Planner Inserts</strong> + How to Use guide
                </div>
              </div>
            </div>
          </div>

          <button 
            className="cta-button large"
            onClick={redirectToVINMETHOD}
          >
            Get VIN METHOD + All Bonuses - $69
          </button>

          <p className="cta-note">
            Complete work-life harmony system designed for your {profileData.name} brain
          </p>

          {/* Secondary CTA - 3-Brain Planning System */}
          <div className="secondary-cta">
            <p className="secondary-cta-text">Not ready for the full system yet?</p>
            <button
              className="secondary-cta-link"
              onClick={redirectTo3BrainSystem}
            >
              Start with The 3-Brain Planning System ($19) →
            </button>
            <p className="secondary-cta-subtext">
              A simpler first step to set up your planner around your {profileData.name} results.
            </p>
          </div>
        </div>

        {/* Download PDF Section */}
        <div className="download-section">
          <h3 className="download-title">📥 SAVE YOUR RESULTS</h3>
          <p className="download-text">
            Bookmark this page or email yourself the link to review your profile anytime.
          </p>
          <p className="email-confirmation">
            Questions? Email us at: <strong>admin@thefunctionalplanning.com</strong>
          </p>
        </div>

        {/* Restart Option */}
        <div className="restart-section">
          <button className="restart-button" onClick={onRestart}>
            ← Start Over
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResultsPage;
